// Thin wrapper over the Notification API. Everything the rest of the app needs
// to know about OS notifications lives here, so tests stub one module and
// unsupported browsers (notably iOS Safari) degrade to a silent no-op.

export type NotificationPermissionState =
  | 'granted'
  | 'denied'
  | 'default'
  | 'unsupported';

function isSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function notificationPermission(): NotificationPermissionState {
  if (!isSupported()) return 'unsupported';
  try {
    return window.Notification.permission as NotificationPermissionState;
  } catch {
    return 'unsupported';
  }
}

export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!isSupported()) return 'unsupported';
  try {
    return (await window.Notification.requestPermission()) as NotificationPermissionState;
  } catch {
    return 'denied';
  }
}

export interface NewOrderNotificationOptions {
  title: string;
  body: string;
  onClick?: () => void;
}

export function showNewOrderNotification({
  title,
  body,
  onClick,
}: NewOrderNotificationOptions): void {
  if (notificationPermission() !== 'granted') return;
  try {
    const notification = new window.Notification(title, {
      body,
      // a stable tag replaces the previous notification instead of stacking
      tag: 'lysi-new-orders',
      icon: '/icons/icon-192.png',
    });
    notification.onclick = () => {
      window.focus();
      onClick?.();
      notification.close();
    };
  } catch {
    /* notifications are a nicety; never let them break the page */
  }
}
